import React, { useCallback, useEffect, useState } from 'react';
import { IconCheck, IconList, IconX } from './icons';

/*
 * QuestionList — the collapsible right-hand "Questions (N)" panel shared by
 * the Flashcards and MCQ players (styles in prepare-up-flashcards.css).
 * Desktop: a side panel with an × to close and a small button to reopen.
 * ≤960px: a drawer over the page (closes on pick, scrim click or Escape).
 */
const LIST_KEY = 'pu_fc_list_v1';   // one remembered open/closed state for both players
export const isNarrow = () => typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(max-width: 960px)').matches;
const loadOpen = () => { if (isNarrow()) return false; try { return localStorage.getItem(LIST_KEY) !== 'closed'; } catch { return true; } };

export const useQuestionList = () => {
  const [open, setOpenState] = useState(loadOpen);
  const setOpen = useCallback((v) => {
    setOpenState((prev) => {
      const next = typeof v === 'function' ? v(prev) : v;
      if (!isNarrow()) { try { localStorage.setItem(LIST_KEY, next ? 'open' : 'closed'); } catch { /* private mode */ } }
      return next;
    });
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && isNarrow()) setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);
  return [open, setOpen];
};

/**
 * items: [{ key, title, sub, state: 'got' | 'again' | null }]
 * total: the count in the heading; pending: rows not loaded yet (shown muted).
 */
const QuestionList = ({ items, current, total, done, pending = 0, open, setOpen, onPick, label = 'question' }) => (
  <>
    {!open && (
      <button type="button" className="fx-list-open" onClick={() => setOpen(true)}
        aria-expanded="false" aria-controls="fx-questions" title="Show questions list" aria-label={`Show questions list (${total})`}>
        <IconList size={18} />
        <span className="fx-list-badge" aria-hidden="true">{total}</span>
      </button>
    )}
    {open && <div className="fx-scrim" onClick={() => setOpen(false)} aria-hidden="true" />}
    {open && (
      <aside id="fx-questions" className="pu-panel fx-questions" aria-label="Questions">
        <div className="fx-questions-head">
          <h2>Questions ({total})</h2>
          <span className="pu-meta">{done} done</span>
          <button type="button" className="fx-close" onClick={() => setOpen(false)} aria-label="Close questions list" title="Close">
            <IconX size={16} />
          </button>
        </div>
        <ol>
          {items.map((it, n) => (
            <li key={it.key}>
              <button type="button" className={`fx-item${n === current ? ' current' : ''}`}
                onClick={() => { onPick(n); if (isNarrow()) setOpen(false); }}
                aria-current={n === current ? 'true' : undefined}
                aria-label={`${label} ${n + 1}: ${it.title}${it.state === 'got' ? ' — correct' : it.state === 'again' ? ' — needs review' : ''}`}>
                <span className={`fx-num${it.state ? ` ${it.state}` : ''}`} aria-hidden="true">
                  {it.state === 'got' ? <IconCheck size={12} /> : n + 1}
                </span>
                <span className="fx-item-text">
                  <b>{it.title}</b>
                  {it.sub && <small>{it.sub}</small>}
                </span>
              </button>
            </li>
          ))}
          {Array.from({ length: pending }).map((_, k) => (
            <li key={`pending-${k}`}>
              <div className="fx-item is-pending" aria-hidden="true">
                <span className="fx-num">{items.length + k + 1}</span>
                <span className="fx-item-text"><b>Upcoming</b><small>Loads when you reach it</small></span>
              </div>
            </li>
          ))}
        </ol>
      </aside>
    )}
  </>
);

export default QuestionList;
